import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/** Page numbers to show: always the first and last, the current one and its neighbours, with gaps as '…'. */
function pageList(page, pages) {
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  const out = [];
  for (let p = 1; p <= pages; p++) {
    if (!keep.has(p)) continue;
    if (out.length && p - out[out.length - 1] > 1) out.push('…');
    out.push(p);
  }
  return out;
}

/** "Showing 11–20 of 47" with previous / page numbers / next. Hidden when everything fits on one page. */
export default function Pagination({ page, pageSize, total, onPage, label = 'items', className = '' }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const btn = 'h-9 min-w-9 px-2.5 rounded-lg border-2 text-sm font-semibold inline-flex items-center justify-center transition-colors';
  return (
    <nav className={`flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 ${className}`} aria-label="Pages" data-testid="pagination">
      <p className="text-sm text-muted-foreground">Showing {from}–{to} of {total} {label}</p>
      <div className="flex items-center gap-1.5">
        <button type="button" className={`${btn} border-ink bg-card hover:bg-butter disabled:opacity-40 disabled:hover:bg-card`}
          onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        </button>
        {pageList(page, pages).map((p, i) => (p === '…'
          ? <span key={`gap-${i}`} className="px-1 text-muted-foreground" aria-hidden="true">…</span>
          : (
            <button key={p} type="button" onClick={() => onPage(p)} aria-current={p === page ? 'page' : undefined}
              className={`${btn} ${p === page ? 'border-ink bg-brand text-[#141414] shadow-neo-sm' : 'border-ink/20 bg-card hover:border-ink'}`}>
              {p}
            </button>
          )))}
        <button type="button" className={`${btn} border-ink bg-card hover:bg-butter disabled:opacity-40 disabled:hover:bg-card`}
          onClick={() => onPage(page + 1)} disabled={page >= pages} aria-label="Next page">
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
